/**
 * DesktopChrome — 데스크탑 웹(≥1024px)의 Astryx SideNav / TopNav.
 *
 * courtyard-demo 의 shell-side-nav 패턴을 peeka 로 옮긴 것. 직접 만든 DesktopNav 를
 * 대체한다 — 섹션·배지·collapsible·resizable 을 Astryx 가 제공한다.
 *
 * 모바일에서는 Layout 이 아예 렌더하지 않는다 (useIsDesktop 분기).
 * 모바일 내비는 기존 BottomNavBar 유지 — 네이티브 앱 관례(하단 탭)를 그대로 둔다.
 */
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TopNav } from "@astryxdesign/core/TopNav";
import { Divider } from "@astryxdesign/core/Divider";
import {
  SideNav,
  SideNavHeading,
  SideNavItem,
  SideNavSection,
} from "@astryxdesign/core/SideNav";
import { NavIcon } from "@astryxdesign/core/NavIcon";
import type { IconType } from "@astryxdesign/core/Icon";
import { HStack } from "@astryxdesign/core/Layout";
import Icon from "../primitives/Icon";
import { useThemeStore } from "../../store/themeStore";
import { THEME_CATALOG, THEME_SWITCHER_ENABLED, findThemeEntry } from "../../theme/themes";
import BrandWordmark from "./BrandWordmark";

export interface DesktopTab {
  path: string;
  label: string;
  /**
   * SideNavItem 은 아이콘 "컴포넌트"와 ReactNode 를 모두 받는다.
   * peeka 는 자체 픽셀 아이콘 세트를 노드로 넘긴다 — 모바일 하단탭과 같은
   * 그림을 쓰기 위해서다 (Layout.tsx 의 TABS 가 정본).
   */
  icon?: ReactNode | IconType;
}

interface DesktopChromeProps {
  tabs: DesktopTab[];
  activeIndex: number;
  onTabTap: (index: number) => void;
}

/** 상단바 — 워드마크 + 검색/알림/설정. 가벼운 슬롯만 둔다(데모 §6 endContent 교훈). */
export function DesktopTopNav() {
  const { t } = useTranslation("card");
  const navigate = useNavigate();
  return (
    <TopNav
      label={t("nav.home")}
      heading={
        /* 워드마크 — 모바일 앱바와 같은 부품을 쓴다. 예전엔 여기만 display-3 글자에
           Sparkles 아이콘을 곁들여, 같은 브랜드가 화면마다 다르게 보였다.
           로고 안에 번개가 이미 있어 아이콘도 뗀다. */
        <BrandWordmark height={28} />
      }
      endContent={
        <HStack gap={1} vAlign="center">
          <button type="button" aria-label={t("nav.search")} onClick={() => navigate("/search")}>
            <Icon name="Search" />
          </button>
          <button type="button" aria-label={t("nav.notifications")} onClick={() => navigate("/notifications")}>
            <Icon name="Notification" />
          </button>
          <button type="button" aria-label={t("nav.me")} onClick={() => navigate("/me")}>
            <Icon name="My" />
          </button>
        </HStack>
      }
    />
  );
}

/** 좌측 내비 — 메인 탭 + (dev) 톤앤매너 전환. */
export function DesktopSideNav({ tabs, activeIndex, onTabTap }: DesktopChromeProps) {
  const { t } = useTranslation("card");
  const navigate = useNavigate();
  const themeKey = useThemeStore((s) => s.themeKey);
  const mode = useThemeStore((s) => s.mode);
  const selectTheme = useThemeStore((s) => s.selectTheme);
  const setMode = useThemeStore((s) => s.setMode);
  const activeTheme = findThemeEntry(themeKey);

  return (
    <SideNav
      collapsible
      resizable={{ defaultWidth: 248, minWidth: 208, maxWidth: 320 }}
      /* 로고 옆 브랜드 마크는 PEEKA 번개다 — 워드마크(BrandWordmark)에 박힌
         번개와 같은 그림이라 상단바·좌측 내비가 한 브랜드로 읽힌다. */
      header={
        <SideNavHeading
          heading="Peeka"
          subheading={t("nav.brandSubtitle")}
          icon={<NavIcon icon={<Icon name="Lightning" color="var(--color-icon-accent)" />} />}
        />
      }
      footer={
        <SideNavSection title={t("nav.me")} isHeaderHidden>
          <SideNavItem label={t("nav.support")} icon={<Icon name="Help" />} onClick={() => navigate("/support")} />
          <SideNavItem label={t("nav.me")} icon={<Icon name="My" />} onClick={() => navigate("/me")} />
        </SideNavSection>
      }
    >
      <SideNavSection title={t("nav.home")} isHeaderHidden>
        {tabs.map((tab, i) => (
          <SideNavItem
            key={tab.path}
            label={tab.label}
            icon={tab.icon}
            isSelected={i === activeIndex}
            onClick={() => onTabTap(i)}
          />
        ))}
      </SideNavSection>

      {/* 톤앤매너 전환 — 개발 빌드에서만. 배포는 y2k 고정이라 이 절이 사라진다. */}
      {THEME_SWITCHER_ENABLED && (
        <>
          <Divider />
          <SideNavSection title={t("nav.appearance")}>
            <SideNavItem label={t("nav.theme", { name: activeTheme.label })} icon={<Icon name="Swatch" />} collapsible>
              {/* ⚠ 파라미터명 t 금지 — useTranslation 의 t 를 가린다 */}
              {THEME_CATALOG.map((entry) => (
                <SideNavItem
                  key={entry.key}
                  label={entry.label}
                  isSelected={themeKey === entry.key}
                  onClick={() => void selectTheme(entry.key)}
                />
              ))}
            </SideNavItem>
            <SideNavItem
              label={t("nav.light")}
              icon={<Icon name="Sun" />}
              isSelected={mode === "light"}
              isDisabled={activeTheme.darkOnly}
              onClick={() => setMode("light")}
            />
            <SideNavItem
              label={t("nav.dark")}
              icon={<Icon name="Moon" />}
              isSelected={mode === "dark"}
              onClick={() => setMode("dark")}
            />
          </SideNavSection>
        </>
      )}
    </SideNav>
  );
}
